import { useState } from 'react';
import { Link } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Clock, X, XCircle } from 'lucide-react';
import { useSubscription } from '@/hooks/use-subscription';
import { SovButton } from '@/components/sovereign/ui/sov-button';

const DISMISS_KEY = 'renewal_reminder_dismissed_until';

function isDismissed(): boolean {
  try {
    return Date.now() < parseInt(sessionStorage.getItem(DISMISS_KEY) ?? '0', 10);
  } catch {
    return false;
  }
}

function persistDismiss() {
  try {
    // Dismiss reminder banner for 24 h (one session-length grace)
    sessionStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
  } catch {}
}

const BANNER_CONFIG: Record<string, { icon: typeof Clock; bg: string; border: string; text: string; getMessage: (days: number) => string }> = {
  reminder: {
    icon: Clock,
    bg: 'bg-brand/5',
    border: 'border-brand/20',
    text: 'text-brand',
    getMessage: (d) => `Your premium subscription renews in ${d} day${d !== 1 ? 's' : ''}. Renew now to keep uninterrupted access.`,
  },
  urgent: {
    icon: AlertTriangle,
    bg: 'bg-amber-400/5',
    border: 'border-amber-400/30',
    text: 'text-amber-400',
    getMessage: (d) => `Your premium expires in ${d} day${d !== 1 ? 's' : ''}! Renew now to avoid losing access.`,
  },
  grace: {
    icon: XCircle,
    bg: 'bg-red-400/8',
    border: 'border-red-400/30',
    text: 'text-red-400',
    getMessage: (d) => `Your subscription has expired. You have ${d} day${d !== 1 ? 's' : ''} of grace period remaining before premium features are locked.`,
  },
};

export function RenewalBanner() {
  const { renewalStatus, daysRemaining, graceDaysRemaining, plan } = useSubscription();
  // Only the low-urgency reminder can be dismissed (urgent/grace are too critical to hide)
  const [dismissed, setDismissed] = useState(() => isDismissed());

  if (plan === 'admin' || plan === 'free') return null;
  if (dismissed && renewalStatus === 'reminder') return null;

  const config = BANNER_CONFIG[renewalStatus];
  if (!config) return null;

  const displayDays = renewalStatus === 'grace' ? graceDaysRemaining : Math.max(0, daysRemaining);
  const Icon = config.icon;
  const canDismiss = renewalStatus === 'reminder';

  const handleDismiss = () => {
    persistDismiss();
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className={`${config.bg} border ${config.border} rounded-xl px-4 py-3 flex items-center gap-3 mx-4 mt-3`}
      >
        <Icon className={`w-5 h-5 ${config.text} shrink-0`} />
        <p className={`text-sm ${config.text} flex-1`}>
          {config.getMessage(displayDays)}
        </p>
        <Link href="/subscribe">
          <SovButton variant={renewalStatus === 'grace' ? 'primary' : 'outline'} size="sm">
            Renew Now
          </SovButton>
        </Link>
        {canDismiss && (
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded text-brand/40 hover:text-brand/80 transition-colors"
            aria-label="Dismiss reminder"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
